import React from 'react';
import * as Sentry from "@sentry/react";
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import DashboardLayout from './components/DashboardLayout';
import Login from './pages/Login';
import DashboardOverview from './pages/DashboardOverview';
import Settings from './pages/Settings';
import Projects from './pages/Projects';
import Board from './pages/Board';
import Backlog from './pages/Backlog';
import History from './pages/History';
import RegressionTracking from './pages/RegressionTracking';
import AuthSuccess from './pages/AuthSuccess';
import SilentAuth from './pages/SilentAuth';
import AcceptInvitation from './pages/AcceptInvitation';
import Subscription from './pages/Subscription';
import Components from './pages/Components';
import E2E from './pages/E2E';
import { ProjectProvider } from './context/ProjectContext';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  
  return <DashboardLayout>{children}</DashboardLayout>;
};

function App() {
  React.useEffect(() => {
    window.name = "viewgate-dashboard";

    const handleAutoScaling = () => {
      // Monitor-agnostic scaling: target a base width for full-size UI
      const targetWidth = 1536; 
      const width = window.innerWidth;
      
      // Auto-scale for desktops between 1024px and 1536px
      if (width < targetWidth && width >= 1024) {
        const scale = width / targetWidth;
        document.documentElement.style.zoom = scale.toString();
      } else {
        document.documentElement.style.zoom = "1";
      }
    };

    handleAutoScaling();
    window.addEventListener('resize', handleAutoScaling);
    return () => window.removeEventListener('resize', handleAutoScaling);
  }, []);

  return (
    <ProjectProvider>
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth-success" element={<AuthSuccess />} />
          <Route path="/silent-auth" element={<SilentAuth />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <DashboardOverview />
            </ProtectedRoute>
          } />
          
          <Route path="/projects" element={
            <ProtectedRoute>
              <Projects />
            </ProtectedRoute>
          } />
          
          <Route path="/board" element={
            <ProtectedRoute>
              <Board />
            </ProtectedRoute>
          } />

          <Route path="/backlog" element={
            <ProtectedRoute>
              <Backlog />
            </ProtectedRoute>
          } />

          <Route path="/history" element={
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          } />

          
          <Route path="/regression-tracking" element={
            <ProtectedRoute>
              <RegressionTracking />
            </ProtectedRoute>
          } />
          
          <Route path="/subscription" element={
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />

          <Route path="/components" element={
            <ProtectedRoute>
              <Components />
            </ProtectedRoute>
          } />

          <Route path="/e2e" element={
            <ProtectedRoute>
              <E2E />
            </ProtectedRoute>
          } />

          {/* Redirect all unknown to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Sentry.ErrorBoundary>
    </ProjectProvider>
  );
}

export default App;
